import React, { useCallback, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Button } from '@bringg/react-components';
import { useHistory } from 'react-router-dom';
import { useRoutesMonitoringTranslations } from '@bringg-frontend/monitoring-shared';
import { View } from 'bringg-web';
import moment from 'moment';
import { debounce } from 'lodash';

import useRouteMonitoringStore from '../stores/helpers/use-route-monitoring-store';
import DocumentationLinkButton from './action-buttons/documentation-link-button';
import { useStores } from 'bringg-web/recipes';
import { hasFeatureFlag } from 'bringg-web/utils/feature-flags';
import FilterButton from '../../shared/components/filter-button/filter-button';
import { userFiltersProvider } from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/utils/user-filters-provider';
import MonitoringFilterBar from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/monitoring-filter-bar';
import {
	FiltersObject,
	FilterBarListItemKeys,
	supportedRunsMonitoringScreenList
} from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/types';
import { monitoringFiltersProvider } from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/utils/monitoring-filter-provider';
import useFiltersManager from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/hooks/use-filters-manager';
import DayPicker from '../../shared/components/day-picker/day-picker';
import { TableDefinition } from '../../shared/stores/groups/table-definition';
import RoutesMonitoringStore from '../stores/routes-monitoring-store';

import styles from './top-section.module.scss';

const getRunTimeWindowErrorStatus = (currentTableDefinition: TableDefinition) =>
	currentTableDefinition
		? !currentTableDefinition?.filters.run[FilterBarListItemKeys.RUN_TIME_WINDOW]?.length
		: false;

const mainFiltersKeys = [FilterBarListItemKeys.RUN_TIME_WINDOW];

const tableDefinitionChangeDebounced = debounce(
	(routesMonitoringStore: RoutesMonitoringStore, filters: FiltersObject) => {
		const normalized = {
			...filters,
			...monitoringFiltersProvider.getNormalizedDateRangeFilters(filters)
		};

		routesMonitoringStore.tableDefinitionChange({
			...monitoringFiltersProvider.splitFiltersToRunsAndTasks(normalized)
		});
	},
	200
);

const TopSection = () => {
	const { goToOrderExplorer } = useRoutesMonitoringTranslations();
	const [filtersCount, setFiltersCount] = useState<number>(null);
	const [showFilters, setShowFilters] = useState<boolean>(false);

	const history = useHistory();
	const {
		usersStore: { currentUser }
	} = useStores();

	const {
		filterBarFiltersWithValues,
		filterObject,
		initialValues,
		initialFilterBarMappedValues,
		initialAdditional,
		setFilterObject
	} = useFiltersManager(userFiltersProvider.getStorageKey(View.routes), supportedRunsMonitoringScreenList);

	const { routesMonitoringStore } = useRouteMonitoringStore();

	const goToOrderExplorerBoard = useCallback(() => {
		history.push({
			pathname: '/order-details'
		});
	}, [history]);

	const hasOrderExplorer = hasFeatureFlag(currentUser, 'enable_order_explorer');

	const onFilterChange = (filters: FiltersObject) => {
		setFiltersCount(monitoringFiltersProvider.count({ filtersObj: filters, keysToIgnore: mainFiltersKeys }));

		tableDefinitionChangeDebounced(routesMonitoringStore, filters);

		setFilterObject(filters);
	};

	useEffect(() => {
		if (!initialValues[FilterBarListItemKeys.RUN_TIME_WINDOW]) {
			initialValues[FilterBarListItemKeys.RUN_TIME_WINDOW] = moment().format('YYYY-MM-DD');
		}

		onFilterChange(initialValues);
	}, [initialValues]);

	return (
		<div className={styles.topSection}>
			<div className={styles.upperSection}>
				<div className={styles.leftContainer}>
					<DocumentationLinkButton />
					{hasOrderExplorer && (
						<Button onClick={goToOrderExplorerBoard} type="link">
							{goToOrderExplorer}
						</Button>
					)}
					<DayPicker
						value={filterObject[FilterBarListItemKeys.RUN_TIME_WINDOW]}
						onChange={day => {
							onFilterChange({ ...filterObject, runTimeWindow: day });
						}}
						errorStatus={getRunTimeWindowErrorStatus(routesMonitoringStore.currentTableDefinition)}
					/>
				</div>
				<FilterButton
					onClick={() => {
						setShowFilters(!showFilters);
					}}
					filterObjectCount={filtersCount}
				/>
			</div>
			<div className={showFilters ? styles.showFilters : styles.hideFilters}>
				<MonitoringFilterBar
					onChange={updatedFilters => {
						const mainFilters: FiltersObject = mainFiltersKeys.reduce((acc, key) => {
							if (filterObject[key]) {
								acc[key] = filterObject[key];
							}

							return acc;
						}, {});

						onFilterChange({ ...updatedFilters, ...mainFilters });
					}}
					filterBarFilters={filterBarFiltersWithValues}
					initialMappedValues={initialFilterBarMappedValues}
					initialAdditional={initialAdditional}
				/>
			</div>
		</div>
	);
};

export default observer(TopSection);
